
import { KitColor } from "@kit/utils/Colors";

/*

Examples of working with AriaColor:

  <div
    class="sb sb-text"
    :style="`height:50px; border:1px solid green; color:#CCCCCC; background:${AriaColor.store(
      '1'
    )
      .seekContrast(
        4.5,
        KitColor.fromCSS('#FF3300'),
        KitColor.fromCSS('#CCCCCC'),
        2
      )
      .background.css()};`"
  >
    Testing Testing X Y Z {{ AriaColor.retrieve("1").seekContrast().contrast }}
  </div>

  <div
    class="sb sb-text"
    :style="`height:50px; border:1px solid green; color:#666666; background:${AriaColor.store(
      '1'
    )
      .seekContrast(
        4.5,
        KitColor.fromCSS('#FF3300'),
        KitColor.fromCSS('#666666'),
        2
      )
      .background.css()};`"
  >
    Testing Testing X Y Z {{ AriaColor.retrieve("1").seekContrast().contrast }}
  </div>

  <div
    class="sb sb-text"
    style="height: 50px; background: #00ffff; color: #ffffff"
  ></div>

  <div
    class="sb sb-text"
    :style="`height:50px; border:1px solid green; color:#FFFFFF; background:${AriaColor.store(
      '1'
    )
      .seekContrast(
        4.5,
        KitColor.fromCSS('#00FFFF'),
        KitColor.fromCSS('#FFFFFF'),
        2
      )
      .background.css()};`"
  >
    Testing Testing X Y Z {{ AriaColor.retrieve("1").seekContrast().contrast }}
  </div>

Literature:

Covers the math, the standards and a contrast checker to help debug
https://www.w3.org/WAI/GL/wiki/Main_Page
https://www.allaccessible.org/blog/color-contrast-accessibility-wcag-guide-2025
https://webaim.org/resources/contrastchecker/
https://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion
https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html#:~:text=Success%20Criterion%20(SC),name%20has%20no%20contrast%20requirement.

From some AI: 

2. The W3C/WCAG Relative Luminance Formula (Accurate)
This is the recommended standard for web accessibility (WCAG 2.x) to determine if a color passes the 4.5:1 ratio threshold. 
DEV Community
DEV Community
 +2
Linearize RGB values:
if RsRGB <= 0.03928 then R = RsRGB/12.92 else R = ((RsRGB+0.055)/1.055) ^ 2.4
(Repeat for G and B)
Calculate Luminance (L):
L = 0.2126 * R + 0.7152 * G + 0.0722 * B
Compare Contrast Ratios:
Against White: (1.05) / (L + 0.05)
Against Black: (L + 0.05) / 0.05
Choose the color with the higher resulting ratio.

*/


//just find out what if white or black is better to show in front of this color
//true if black 
//false if white
export const getBasicContastBlackOrWhite = ({ r, g, b}) => {
  const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return l > 0.5 ? true : false
}

 
//rgb: { r:[0-255], g:[0-255], b:[0-255] }
const ariaRelLuminanceHelper = (t) => {
  if(t <= 0.03928) {
    return t/12.92 
  } else {
    return Math.pow( ((t+0.055)/1.055), 2.4)
  }
}

//Use the relative luminance that aria uses.
//From https://www.w3.org/WAI/GL/wiki/Relative_luminance
export const ariaRelativeLuminance = ({ r, g, b }) => {
  
  r/=255 
  g/=255
  b/=255

  const _r = ariaRelLuminanceHelper(r) 
  const _g = ariaRelLuminanceHelper(g)
  const _b = ariaRelLuminanceHelper(b)

  const l = 0.2126*_r + 0.7152*_g + 0.0722*_b 

  return l

}

/**
 * https://www.allaccessible.org/blog/color-contrast-accessibility-wcag-guide-2025

  Contrast Ratio = (L1 + 0.05) / (L2 + 0.05)

  Where:
  - L1 = relative luminance of the lighter color
  - L2 = relative luminance of the darker color
  - Range: 1:1 (no contrast) to 21:1 (maximum contrast)

  Get the contrast ratio

  @param {KitColor} l1RBG
  @param {KitColor} l2RGB

  returns number. Contrast Ratio

*/
export const ariaContrastRatio = (l1RGB, l2RGB) => {

  const l1 = ariaRelativeLuminance(l1RGB)
  const l2 = ariaRelativeLuminance(l2RGB)

  let lighter, darker = null 

  lighter = l1 >= l2 ? l1 : l2
  darker =  l1 >= l2 ? l2 : l1

  const cR = (lighter + 0.05) / (darker + 0.05)

  // let lC,dC = null
  // lC = l1 >= l2 ? l1RGB : l2RGB
  // dC =  l1 >= l2 ? l2RGB : l1RGB
  // console.log("lighter color: "+lC.css()+" darker: "+dC.css()+" "+cR)

  return cR
}


export const ariaIsLighterOrDarker = (isThis, lighterThanThat) => {
  const l1 = ariaRelativeLuminance(isThis)
  const l2 = ariaRelativeLuminance(lighterThanThat)

  if(l1 > l2) {
    return true
  }
  return false
}


/** 

In the middle, v: [0, 70] the saturation should increase.
We set the parameters for the sx to find the saturation value, and 
we set the power p to be 1 so that it increases linerly.

Then when the v is [70, 100], it should decrease. 
We set the parameters for the sx to find the saturation value, and 
we set the power p to be 2 so that it decreases quadratically.
The choice of linerar vs quadratic is for aesthetics it just seems to look better.

You can graph these yourself:
https://www.desmos.com/calculator
y\ =\ 80\ +\ \left(10\ -\ 80\right)\cdot\left(\frac{\left(\ x\ -\ 40\right)}{\left(80\ -\ 40\right)}\right)^{2}

@param {Number "v" from hsv [0-100]} required. The v value

@returns {saturation "s" from hsv [0-100]}
The saturation to help make the color contrast more against text of opposite disposition
i.e. dark text against a light background.

*/
const calculateSaturation = (v) => {

  let si, sf, sx, vi, vf, vx, ds, dv, p = 0

  //The v where it stops going up linearly and starts going down quadratically
  const interfaceValue = 70

  if(v >= 0 && v < interfaceValue) {
    si = 0 
    sf = 100
    vi = 0 
    vf = interfaceValue
    p = 1
  } else {
    si = 100
    sf = 0
    vi = interfaceValue
    vf = 100
    p = 2
  }

  dv = vf - vi
  vx = v - vi 
  ds = sf - si

  sx = si + ds*Math.pow(vx/dv, p)

  return sx
}

class _AriaColor {

  static _store_seekColorWithContrast = {}
  static _storeNextResultWithKey = null;
  static _retrieveNextResultWithKey = null;

  /**
   * Use this in templating to store a result so that another part of the template can use it,
   * 
   * AriaColor.store("foo").seekContrast(4.5, KitColor.fromCSS("#FF0000"))
   * 
   * Then, later on in your template you can do this:
   * 
   * AriaColor.retrieve("foo").seekContrast()
   * And it will give you the result from the stored call
   * 
   * @param {String} key. required. 
   * @returns The class.
   */
  static store(_key) {
    const key = _key || "1"
    _AriaColor._storeNextResultWithKey = key
    return _AriaColor
  }
  static retrieve(_key) {
    const key = _key || "1"
    _AriaColor._retrieveNextResultWithKey = key 
    return _AriaColor
  }

  /**
   * @param {Number [0 -]} targetContrast. Required, the required relative contrast ratio (see links below)
   * @param {KitColor} backgroundcolor. Required. The background color that text of a different color is supposed to contrast against
   * @param {KitColor} textColor. Optional. If included, it will perform a simple test to auto-detect if the text should
   *   be black or white and proceed from there. If included, it will try to seek a contrasting color against the text-color.
   * 
   * @param {Integer 0 - 2 Default is 0} _errMode. Optional. 
   * If 0, then failure to find a contrasting color will result in null.
   * If 1, then failure to find a contrasting color will result in an error
   * If 2, then failure to fina a contrastng color will just break the loop and it will 
   *   output whatever the last calculated color was.
   * 
   * This function will stepwise mutate the background color's value and saturation in the hsv color model in order to 
   * find a suitable background color that will clear wai-aria's contrast threshold. 
   * 
   * https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html#:~:text=Success%20Criterion%20(SC),name%20has%20no%20contrast%20requirement.
   * https://webaim.org/resources/contrastchecker/
   * https://www.allaccessible.org/blog/color-contrast-accessibility-wcag-guide-2025
   * 
   */
  static seekContrast(targetContrast, backgroundColor, textColor, _errMode, _safetyLim) {

    //It there's no target contrast then we assume that it's being 
    //used in a retrieve mode and so we're going to get the result from the store
    //and zero out the key
    if(!targetContrast) {
      const key = _AriaColor._retrieveNextResultWithKey
      if(key) {
        _AriaColor._retrieveNextResultWithKey = null
        const stored = _AriaColor._store_seekColorWithContrast[key]
        return stored
      }
    }

    const limit = _safetyLim || 100
    const errMode = _errMode || 0

    let fixedColor = textColor 
    let isFixedColorDarker = false

    if(!textColor) {
      const isTextBlack = getBasicContastBlackOrWhite(backgroundColor)
      fixedColor = isTextBlack ? KitColor.fromRGB({r:0,g:0,b:0}) : KitColor.fromRGB({r:255,g:255,b:255})
      isFixedColorDarker = isTextBlack
    } else {
      isFixedColorDarker = ariaIsLighterOrDarker(backgroundColor, textColor)
    }

    const variableColor = backgroundColor.clone()


    //if the text is black, then we're going to make the color lighter.
    //else, if the text is white, then we're going to make it darker
    const strategy = isFixedColorDarker
    ? (variableColor) => {

      const v = variableColor.hsv().v

      variableColor.hsvDelta({ dv: 5 })

      //if the hsv value is less than 90, then we're going to apply
      //our calculated saturation profile
      if(v < 90) {
        const s = calculateSaturation(v)
        variableColor.S(s)       
      } 
      //But if the value is really close to 100, then the saturation is going to
      //just be quickly tending towards zero. What's going to happen is that 
      //we run out of v, and it hits 100 really soon, but we still have some saturtion
      //that we can play with, so as we run out of v and v maxes out at 100, we're going
      //to start decrease the saturation gradually instead of letting it tend to zero immediately.
      else {
        variableColor.hsvDelta({ ds: -5 })
      }

    }
    : (variableColor) => {

      //This one is easier because we just want it to get darker.
      //maybe we can do some other fixing like the case above but this looks alright for now.
      const v = variableColor.hsv().v
      const s = calculateSaturation(v)

      variableColor.hsvDelta({ dv: -5 })
      variableColor.S(s)

    }

    let safety = 0
    let contrast = 0

    while(true) {

      safety++
      let broken = false

      if(safety > limit) {
        if(errMode == 0) {
          return null
        } else 
        if(errMode == 1) {
          throw new Error("Cannot find color within "+limit+" steps")
        } else {
          console.log('WARNING: 540349 AriaColors: I was unable to find a contrasting background color text: '+textColor.css()+' starting from the color: '+backgroundColor.css()+" to meet the threshold: "+targetContrast)
          broken = true
        }
      }

      if(broken) {
        break
      } else {

      const cR = ariaContrastRatio(fixedColor, variableColor)
      contrast = cR

        if(cR < targetContrast) {
          strategy(variableColor)
        } else {
          break
        }

      }

    }

    const result = { background: variableColor, text:fixedColor, contrast } 

    const key = _AriaColor._storeNextResultWithKey
    if(key) {
      //clear out what was there before and now store the new one.
      _AriaColor._store_seekColorWithContrast[key] = null
      _AriaColor._store_seekColorWithContrast[key] = result
      _AriaColor._storeNextResultWithKey = null
    }

    return result
  }

}

export const AriaColor = _AriaColor